import React, { useState, useEffect } from 'react';
import Icon from '../../../components/AppIcon';
import { useWebsiteData } from '../../../context/WebsiteDataContext';

const TestimonialCarousel = () => {
  const { getPublishedTestimonials } = useWebsiteData();
  const testimonials = getPublishedTestimonials();
  const [currentIndex, setCurrentIndex] = useState(0);
  const [isAutoPlaying, setIsAutoPlaying] = useState(true);
  useEffect(() => {
    if (!isAutoPlaying) return;

    const interval = setInterval(() => {
      setCurrentIndex((prevIndex) =>
        prevIndex === testimonials.length - 1 ? 0 : prevIndex + 1
      );
    }, 5000);

    return () => clearInterval(interval);
  }, [isAutoPlaying, testimonials.length]);

  const goToSlide = (index) => {
    setCurrentIndex(index);
    setIsAutoPlaying(false);
    setTimeout(() => setIsAutoPlaying(true), 10000);
  };

  const goToPrevious = () => {
    setCurrentIndex(currentIndex === 0 ? testimonials.length - 1 : currentIndex - 1);
    setIsAutoPlaying(false);
    setTimeout(() => setIsAutoPlaying(true), 10000);
  };

  const goToNext = () => {
    setCurrentIndex(currentIndex === testimonials.length - 1 ? 0 : currentIndex + 1);
    setIsAutoPlaying(false);
    setTimeout(() => setIsAutoPlaying(true), 10000);
  };

  const currentTestimonial = testimonials[currentIndex];

  if (!currentTestimonial) {
    return null;
  }

  return (
    <section id="testimonials" className="py-16 bg-muted/10">
      <div className="container mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="text-center mb-12">
          <div className="inline-flex items-center space-x-2 bg-custom-brown/10 rounded-full px-4 py-2 mb-4">
            <Icon name="MessageSquare" size={16} className="text-custom-brown" />
            <span className="text-custom-brown text-sm font-medium">Client Success Stories</span>
          </div>
          <h2 className="font-headline text-3xl sm:text-4xl font-bold text-foreground mb-4">
            Real Transformations, Real Results
          </h2>
          <p className="font-body text-lg text-muted-foreground max-w-2xl mx-auto">
            Discover how Nirmitee has helped clients across Pune turn their construction dreams into reality.
          </p>
        </div>

        {/* Main Testimonial */}
        <div className="max-w-6xl mx-auto">
          <div className="max-w-4xl mx-auto">
            <div className="bg-card rounded-2xl construction-shadow-lg p-8 lg:p-12 relative overflow-hidden">
              {/* Decorative Quote Icon Background */}
              <div className="absolute top-0 right-0 -mt-8 -mr-8 text-custom-brown/5 opacity-50 transform rotate-12">
                <Icon name="Quote" size={200} />
              </div>

              <div className="relative z-10 flex flex-col items-center text-center">
                {/* Rating */}
                <div className="flex items-center space-x-1 mb-6">
                  {[...Array(5)].map((_, i) => (
                    <Icon
                      key={i}
                      name="Star"
                      size={24}
                      className={`${i < currentTestimonial.rating ? 'text-custom-brown fill-current' : 'text-gray-300'}`}
                    />
                  ))}
                </div>

                {/* Testimonial Text */}
                <blockquote className="text-xl md:text-2xl font-body text-foreground leading-relaxed mb-8 italic">
                  "{currentTestimonial.testimonial}"
                </blockquote>

                {/* Client Details */}
                <div className="flex flex-col items-center">
                  <h3 className="font-headline text-xl font-bold text-foreground mb-1">
                    {currentTestimonial.clientName}
                  </h3>
                  <p className="text-muted-foreground font-medium mb-1">
                    {currentTestimonial.company}
                  </p>
                  <div className="inline-flex items-center px-3 py-1 rounded-full bg-custom-brown/10 text-custom-brown text-sm font-medium mt-2">
                    <Icon name="Briefcase" size={14} className="mr-2" />
                    {currentTestimonial.service}
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Navigation */}
          <div className="flex items-center justify-between mt-8">
            {/* Previous Button */}
            <button
              onClick={goToPrevious}
              className="flex items-center justify-center w-12 h-12 bg-card rounded-full construction-shadow hover:construction-shadow-lg transition-construction"
            >
              <Icon name="ChevronLeft" size={20} className="text-foreground" />
            </button>

            {/* Dots Indicator */}
            <div className="flex space-x-2">
              {testimonials.map((_, index) => (
                <button
                  key={index}
                  onClick={() => goToSlide(index)}
                  className={`w-3 h-3 rounded-full transition-construction ${index === currentIndex
                    ? 'bg-primary' : 'bg-muted hover:bg-muted-foreground/30'
                    }`}
                />
              ))}
            </div>

            {/* Next Button */}
            <button
              onClick={goToNext}
              className="flex items-center justify-center w-12 h-12 bg-card rounded-full construction-shadow hover:construction-shadow-lg transition-construction"
            >
              <Icon name="ChevronRight" size={20} className="text-foreground" />
            </button>
          </div>

          {/* Auto-play Indicator */}
          <div className="text-center mt-4">
            <div className="inline-flex items-center space-x-2 text-xs text-muted-foreground">
              <div className={`w-2 h-2 rounded-full ${isAutoPlaying ? 'bg-accent animate-pulse' : 'bg-muted'}`}></div>
              <span>{isAutoPlaying ? 'Auto-playing' : 'Paused'}</span>
            </div>
          </div>
        </div>

        {/* Testimonial Stats */}
        <div className="mt-16 grid md:grid-cols-4 gap-6">
          <div className="text-center">
            <div className="text-2xl font-bold text-primary mb-2">4.9/5</div>
            <div className="text-sm text-muted-foreground">Average Rating</div>
          </div>
          <div className="text-center">
            <div className="text-2xl font-bold text-secondary mb-2">500+</div>
            <div className="text-sm text-muted-foreground">Happy Clients</div>
          </div>
          <div className="text-center">
            <div className="text-2xl font-bold text-accent mb-2">₹2Cr+</div>
            <div className="text-sm text-muted-foreground">Total Savings</div>
          </div>
          <div className="text-center">
            <div className="text-2xl font-bold text-warning mb-2">98%</div>
            <div className="text-sm text-muted-foreground">Success Rate</div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default TestimonialCarousel;